'use client';

import React, { useState, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useForm, FormProvider } from 'react-hook-form';
import { Form } from '@/components/ui/form';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SecondaryPackageDetails from './secondary-package-details';
import TertiaryPackageDetails from './tertiary-package-details';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useUpdateRequestItemMutation } from '@/redux/slices/stack-and-ship/stack-and-ship-apiSlice';
import { useStepperContext } from '@/pages/distribution-analytics/components/stepper-provider';
import { Input } from '@/components/ui/input';
import { FormField, FormMessage, FormControl, FormItem } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from '@/components/ui/button';
import { showErrorToast } from '@/components/toast/toast';
import { XCircle } from 'lucide-react';

interface PackageContentProps {
    requestItem?: {
        has_tertiary_package?: string;
        [key: string]: any;
    };
}

const initialValues = {
    secondary_package_type: '',
    secondary_topload_filled: '',
    secondary_topload_filled_unit: 'lbf',
    secondary_weight: '',
    secondary_weight_unit: '',
    secondary_length: '',
    secondary_length_unit: 'inch',
    secondary_width: '',
    secondary_width_unit: 'inch',
    secondary_height: '',
    secondary_height_unit: "inch",
    isSecondaryLengthAutoCalculated:true,
    isSecondaryWidthAutoCalculated:true,
    isSecondaryHeightAutoCalculated:true,
    isSecondaryWeightAutoCalculated:true,
    tertiary_material: "",
    tertiary_bct: "",
    tertiary_bct_unit: '',
    tertiary_length: "",
    tertiary_length_unit: "inch",
    tertiary_width: "",
    tertiary_width_unit: "inch",
    tertiary_height: "",
    tertiary_height_unit: "inch",
    tertiary_weight: "",
    tertiary_weight_unit: "lbs",
    isTertiaryLengthAutoCalculated:true,
    isTertiaryWidthAutoCalculated:true,
    isTertiaryHeightAutoCalculated:true,
    isTertiaryWeightAutoCalculated:true,
    pallet_pattern: '',
    number_of_layers: '',
    cases_per_layer: '',
    k_value: ''
}

const palletPatternOptions = [
    'Split Row',
    'Pin Wheel',
    'Split Pin Wheel',
    'Split Block',
    'Row',
    '12B Offset',
    'Brick',
    'Others',
];

const optionalUnitLoadField = z.preprocess(
    (value) => value ?? '',
    z.string().optional()
);

const formSchema = z.object({
    secondary_package_type: z.string().min(1, 'Secondary package type is required'),
    secondary_topload_filled: z.nullable(z.string()),
    secondary_topload_filled_unit: z.nullable(z.string()),
    secondary_weight: z.string().min(1, 'Secondary weight is required').refine((val) => Number(val) > 0, 'Value must be positive'),
    secondary_weight_unit: z.string(),
    secondary_length: z.string().min(1, 'Secondary length is required').refine((val) => Number(val) > 0, 'Value must be positive'),
    secondary_length_unit: z.string(),
    isSecondaryLengthAutoCalculated:z.boolean(),
    isSecondaryWidthAutoCalculated:z.boolean(),
    isSecondaryHeightAutoCalculated:z.boolean(),
    isSecondaryWeightAutoCalculated:z.boolean(),
    secondary_width: z.string()
        .min(1, 'Secondary width is required')
        .refine((val) => Number(val) > 0, 'Value must be positive'),
    secondary_width_unit: z.string(),
    secondary_height: z.string()
        .min(1, 'Secondary height is required')
        .refine((val) => Number(val) > 0, 'Value must be positive'),
    secondary_height_unit: z.string(),

    pallet_pattern: optionalUnitLoadField,
    number_of_layers: optionalUnitLoadField,
    cases_per_layer: optionalUnitLoadField,
    k_value: z.string().min(1, 'This value is required')
})

const bothSchema = z.object({
    secondary_package_type: z.string().min(1, 'Secondary package type is required'),
    secondary_topload_filled: z.nullable(z.string()),
    secondary_topload_filled_unit: z.nullable(z.string()),
    secondary_weight: z.string().min(1, 'Secondary weight is required').refine((val) => Number(val) > 0, 'Value must be positive'),
    secondary_weight_unit: z.string(),
    secondary_length: z.string().min(1, 'Secondary length is required').refine((val) => Number(val) > 0, 'Value must be positive'),
    secondary_length_unit: z.string(),
    secondary_width: z.string()
        .min(1, 'Secondary width is required')
        .refine((val) => Number(val) > 0, 'Value must be positive'),
    secondary_width_unit: z.string(),
    secondary_height: z.string()
        .min(1, 'Secondary height is required')
        .refine((val) => Number(val) > 0, 'Value must be positive'),
    secondary_height_unit: z.string(),
    isSecondaryLengthAutoCalculated:z.boolean(),
    isSecondaryWidthAutoCalculated:z.boolean(),
    isSecondaryHeightAutoCalculated:z.boolean(),
    isSecondaryWeightAutoCalculated:z.boolean(),
    tertiary_material: z.string().min(1, 'Tertiary material is required'),
    tertiary_bct: z.nullable(z.string()),
    tertiary_bct_unit: z.nullable(z.string()),
    tertiary_length: z.string()
        .min(1, 'Tertiary length is required')
        .refine((val) => Number(val) > 0, 'Value must be positive'),
    tertiary_length_unit: z.string(),
    tertiary_width: z.string()
        .min(1, 'Tertiary width is required')
        .refine((val) => Number(val) > 0, 'Value must be positive'),
    tertiary_width_unit: z.string(),
    tertiary_height: z.string()
        .min(1, 'Tertiary height is required')
        .refine((val) => Number(val) > 0, 'Value must be positive'),
    tertiary_height_unit: z.string(),
    tertiary_weight: z.string()
        .min(1, 'Tertiary weight is required')
        .refine((val) => Number(val) > 0, 'Value must be positive'),
    tertiary_weight_unit: z.string(),
    pallet_pattern: optionalUnitLoadField,
    number_of_layers: optionalUnitLoadField,
    cases_per_layer: optionalUnitLoadField,
    k_value: z.string().min(1, 'This value is required'),
    isTertiaryLengthAutoCalculated:z.boolean(),
    isTertiaryWidthAutoCalculated:z.boolean(),
    isTertiaryHeightAutoCalculated:z.boolean(),
    isTertiaryWeightAutoCalculated:z.boolean(),
})

const PackageContent: React.FC<PackageContentProps> = ({ requestItem = {}, refetch }) => {
    const [hasLoaded, setHasLoaded] = useState(false);
    const [initialFormValues, setInitialFormValues] = useState<Record<string, any>>(null);
    const [hasFormChanged, setHasFormChanged] = useState(false);

    const methods = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(requestItem.has_tertiary_package === 'yes' ? bothSchema : formSchema),
        defaultValues: {
            ...initialValues,
            ...Object.keys(initialValues).reduce((acc, key) => {
                if (requestItem && key in requestItem) {
                    acc[key] = ['pallet_pattern', 'number_of_layers', 'cases_per_layer'].includes(key) && requestItem[key] == null
                        ? ''
                        : requestItem[key];
                }
                // Set default units if value is null/undefined and key contains 'unit'
                if ((!acc[key] || acc[key] === null || acc[key] === undefined) && key.includes('unit')) {
                    if (key.includes('weight')) {
                        acc[key] = 'lbs';
                    }
                    else if (key.includes('tertiary_bct_unit') || key.includes('secondary_topload_filled_unit')) {
                        acc[key] = 'lbf';
                    }
                    else {
                        acc[key] = 'inch';
                    }
                }
                return acc;
            }, {} as Record<string, any>)
        },
        mode: "onChange",
    });

    // Save initial form values after initial load
    useEffect(() => {
        if (!hasLoaded) {
            const currentValues = methods.getValues();
            setInitialFormValues(currentValues);
            setHasLoaded(true);
        }
    }, [methods, hasLoaded]);

    // Subscribe to form changes to detect modifications
    useEffect(() => {
        const subscription = methods.watch(() => {
            if (hasLoaded && initialFormValues) {
                // Compare current form values with initial values
                const currentValues = methods.getValues();
                const hasChanged = Object.keys(currentValues).some(key => {
                    // Handle empty strings vs null/undefined correctly
                    const initialValue = initialFormValues[key] || '';
                    const currentValue = currentValues[key] || '';
                    return initialValue !== currentValue;
                });

                setHasFormChanged(hasChanged);
            }
        });

        return () => subscription.unsubscribe();
    }, [methods, hasLoaded, initialFormValues]);

    const [updateRequestItem, { isLoading: isUpdating, }] = useUpdateRequestItemMutation();
    const { handleNext } = useStepperContext();

    const onSubmit = async (data: z.infer<typeof formSchema>) => {
        // Only proceed if the form has changes
        if (hasFormChanged) {
            try {
                await updateRequestItem({ data, id: requestItem.id }).unwrap();
                await refetch?.();
                // Update initial values after successful submission
                setInitialFormValues(methods.getValues());
                setHasFormChanged(false);
                handleNext();
            } catch (error) {
                console.error('Error updating request item:', error);
                const apiError = error as { data?: { message?: string; error?: string }; message?: string };
                showErrorToast(
                    apiError.data?.message
                    || apiError.data?.error
                    || apiError.message
                    || 'Unable to save package details'
                );
            }
        } else {
            // No changes, just proceed to next step
            handleNext();
        }
    };
    
    const hasTertiaryPackage = requestItem.has_tertiary_package === 'yes' ? 'yes' : 'no';

    const clearUnitLoadDetails = () => {
        methods.setValue('pallet_pattern', '', { shouldDirty: true, shouldTouch: true, shouldValidate: true });
        methods.setValue('number_of_layers', '', { shouldDirty: true, shouldTouch: true, shouldValidate: true });
        methods.setValue('cases_per_layer', '', { shouldDirty: true, shouldTouch: true, shouldValidate: true });
        setHasFormChanged(true);
    };

    const renderUnitLoadDetails = () => (
        <div className="mt-6 grid grid-cols-1 gap-4 border border-[#DCE7F5] bg-[#F7FAFE] p-4 md:grid-cols-3">
            <div className="md:col-span-3 flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-base font-bold text-[#26384D]">Unit Load Details (Optional)</h3>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-[#004899] text-[#004899]"
                    onClick={clearUnitLoadDetails}
                    aria-label="Clear unit load details"
                >
                    <XCircle className="h-4 w-4" />
                    Clear
                </Button>
            </div>
            <div className="md:col-span-3 text-sm text-red-600">
                Use these optional pallet parameters only if the pallet pattern design has already been finalized.
                If left empty, similar package configurations will still be shown.
            </div>
            <div>
                <label className="mb-2 block text-sm font-semibold text-[#26384D]">Pallet Pattern Design</label>
                <FormField
                    control={methods.control}
                    name="pallet_pattern"
                    render={({ field }) => (
                        <FormItem>
                            <Select value={field.value || ''} onValueChange={field.onChange}>
                                <FormControl>
                                    <SelectTrigger className="border-2 bg-selectBlue">
                                        <SelectValue placeholder="Select pallet pattern" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    {palletPatternOptions.map((option) => (
                                        <SelectItem key={option} value={option}>
                                            {option}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>
            <div>
                <label className="mb-2 block text-sm font-semibold text-[#26384D]">Number Of Layers</label>
                <FormField
                    control={methods.control}
                    name="number_of_layers"
                    render={({ field }) => (
                        <FormItem>
                            <FormControl>
                                <Input type="number" placeholder="e.g. 7" {...field} value={field.value ?? ''} className="border-2 bg-bgYellow" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>
            <div>
                <label className="mb-2 block text-sm font-semibold text-[#26384D]">Cases Per Layer</label>
                <FormField
                    control={methods.control}
                    name="cases_per_layer"
                    render={({ field }) => (
                        <FormItem>
                            <FormControl>
                                <Input type="number" placeholder="e.g. 21" {...field} value={field.value ?? ''} className="border-2 bg-bgYellow" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>
        </div>
    );

    const renderRequiredSimilarSpecifications = () => (
        <div className="mt-4 max-w-md border border-[#DCE7F5] bg-white p-4">
            <label className="mb-2 block text-sm font-semibold text-[#26384D]">Required Similar Specifications</label>
            <FormField
                control={methods.control}
                name="k_value"
                render={({ field }) => (
                    <FormItem>
                        <FormControl>
                            <Input type="number" placeholder="Enter count" {...field} className="border-2" />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />
        </div>
    );

    return (
        <Form {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)} className="w-full max-w-7xl mx-auto px-4 py-6">
                <Tabs defaultValue="secondary" className="w-full">
                    <div className="border-b mb-6">
                        <TabsList className="w-full justify-start gap-8 h-14 bg-transparent">
                            <TabsTrigger
                                value="secondary"
                                className="data-[state=active]:border-b-2 data-[state=active]:border-blue data-[state=active]:shadow-none px-6 text-lg font-medium"
                            >
                                Secondary Package Details
                            </TabsTrigger>
                            {hasTertiaryPackage === 'yes' && (
                                <TabsTrigger
                                    value="tertiary"
                                    className="data-[state=active]:border-b-2 data-[state=active]:border-blue data-[state=active]:shadow-none px-6 text-lg font-medium"
                                    disabled={hasTertiaryPackage !== 'yes'}
                                >
                                    Tertiary Package Details
                                </TabsTrigger>
                            )}
                        </TabsList>
                    </div>
                    <div className="mt-6">
                        <TabsContent value="secondary" className="mt-0">
                            <SecondaryPackageDetails form={methods} request={requestItem} />
                            {hasTertiaryPackage !== 'yes' && (
                                <>
                                    {renderUnitLoadDetails()}
                                    {renderRequiredSimilarSpecifications()}
                                </>
                            )}
                        </TabsContent>
                        <TabsContent value="tertiary" className="mt-0">
                            {hasTertiaryPackage === 'yes' && (
                                <>
                                    <TertiaryPackageDetails request={requestItem} form={methods} />
                                    {renderUnitLoadDetails()}
                                    {renderRequiredSimilarSpecifications()}
                                </>
                            )}
                        </TabsContent>
                    </div>
                </Tabs>
                <button
                    id='submit-button'
                    type="submit"
                    style={{ display: 'none' }}
                    title="Submit form"
                />
            </form>
        </Form>
    );
};

export default PackageContent;
